import { useRecoilValue } from "recoil";
import { checkOutOfBounds, getCurrGeoLevelName, isAddedToCompare, subGeoLevel } from "../../helpers";
import { compareListState, geoState, ptypeState, selectedTabState } from "../../states";
import { Button, ButtonSize, ButtonTheme, ButtonVariant, Heading, TypographyColor, TypographyType } from "../../ui-elements";
import { RESET_FIELD_ARRAY } from "../../constants";
import { useSearchParams } from "react-router-dom";
import { AddToCompare } from "./AddToCompare";
import { LuArrowUpRight } from "react-icons/lu";

interface Props {
    classname: string;
    presence: any;
    company_name: string;
    focusedPoi: any
    handleClose: (e: any) => void;
    handleNavigation: (e: any, geoLevel: string, geoId: any, geoLevelName: string) => void;
    roi: any;
    carousel?: {
        type: string;
        markers: any[];
        currentIndex: number;
        uniqueKeyField: string;
    } | null;
    onCarouselNext?: () => void;
    onCarouselPrev?: () => void;
}

export const PresenceHoverCard: React.FC<Props> = ({
    classname,
    presence,
    company_name,
    focusedPoi,
    handleClose,
    handleNavigation,
    roi,
    carousel,
    onCarouselNext,
    onCarouselPrev
}) => {
    const selectedGeo = useRecoilValue(geoState);
    const selectedTab = useRecoilValue(selectedTabState);
    const [searchParams] = useSearchParams();
    const catchment = searchParams.get('catchment');
    const compareList = useRecoilValue(compareListState);

    // Computed conditions for better readability
    const isMbpFocused = focusedPoi.type === 'mbp' && focusedPoi?.data?.unique_key === presence?.unique_key;
    const isNotMbpFocused = focusedPoi.type !== 'mbp' && focusedPoi?.data?.unique_key !== presence?.unique_key;
    const hasCatchment = presence?.navigation?.catchment && catchment != presence?.navigation?.catchment;
    const shouldShowCatchmentButtons = (presence?.geoLevel !== 'catchment' && (catchment || !selectedGeo.village)) || catchment != presence?.navigation?.catchment;
    const isNotCatchmentGeoLevel = presence?.geoLevel !== 'catchment';
    const showVisitButton = presence?.geoLevel !== 'catchment' && !selectedGeo.village && !hasCatchment;
    const showCatchmentActions = shouldShowCatchmentButtons && hasCatchment;
    const hasMultipleButtonGroups = showCatchmentActions && showVisitButton;

    // Reusable styles
    const smallTextStyle = { fontSize: "10px" };
    const carouselStyle = {
        backgroundColor: "white",
        position: "absolute" as const,
        bottom: "-15%",
        left: "30%",
        margin: "0 auto",
        borderRadius: "8px",
        width: "45%"
    };

    const handleVisitRegion = (e: any, geoLevel?: string, isRegion?:boolean) => {
        if (geoLevel === "catchment") {
            handleNavigation(e, RESET_FIELD_ARRAY[geoLevel], presence?.navigation, geoLevel);
            return;
        }
        const currLevel = getCurrGeoLevelName(selectedGeo);
        geoLevel = "";
        if (catchment)
            geoLevel = "all";
        else if (presence?.geoLevel === 'catchment' && !isRegion)
            geoLevel = "catchment";
        else if (currLevel === 'sub_district')
            geoLevel = "village";
        else if (currLevel === 'district')
            geoLevel = selectedTab === 4 ? "sub_district" : "pincode";
        else if (currLevel === 'state')
            {geoLevel = "district";}

        // If isRegion is true, remove catchment key from navigation object
        let navigationData = presence?.navigation;
        if (isRegion && presence?.navigation) {
            const { catchment, ...rest } = presence.navigation;
            navigationData = rest;
        }
        handleNavigation(e, RESET_FIELD_ARRAY[geoLevel], navigationData, isRegion ? geoLevel : presence?.geoLevel);
    }

    return (
        <div
            className={`d-flex flex-column padding-top-bottom-2 ${classname} pe-none ${presence.geoLevel === 'catchment' ? '' : checkOutOfBounds({ lat: presence?.latitude, lng: presence?.longitude }, roi).join(" ")}`}
        >
            <div className="d-flex align-items-center justify-content-between gap-1">
                <Heading
                    title={company_name}
                    type={TypographyType.h7}
                    colour={TypographyColor.primary}
                    classname='text-start m-0 p-2 padding-top-bottom-1'
                />
                {isMbpFocused && (
                    <span
                        className="material-symbols-outlined fs-16 color-black m-0 padding-right-2 cursor-pointer pe-auto"
                        onClick={(e) => handleClose(e)}
                    >
                        close
                    </span>
                )}
            </div>
            <div className="d-flex justify-content-start align-items-center gap-1 padding-left-right-2">
                <img src={presence.presence_type_icon} alt={presence.presence_type} />
                <Heading
                    style={smallTextStyle}
                    title={`${presence.presence_type}: ${presence.branch_name || ''}`}
                    type={TypographyType.h7}
                    colour={TypographyColor.secondary}
                    classname='align-middle m-0 p-0'
                />
            </div>

            <hr className="m-0 p-0 margin-top-1 margin-bottom-3" />

            {/* Catchment region info - shown when MBP is focused */}
            {hasCatchment && isMbpFocused && !selectedGeo?.village && (
                <>
                    <div className="d-flex justify-content-between padding-bottom-2 padding-left-2">
                        <div>
                            <Heading
                                title={
                                    (catchment ? presence?.regionName?.village : !selectedGeo?.district
                                        ? presence?.regionName?.district
                                        : !selectedGeo?.sub_district
                                            ? (selectedTab == 4 ? presence?.regionName?.sub_district : presence?.regionName?.pincode)
                                            : presence?.regionName?.village)
                                }                                
                                type={TypographyType.h7}
                                colour={TypographyColor.primary}
                                classname='text-start m-0 padding-top-1'
                                style={smallTextStyle}
                            />
                            <Heading
                                title={`${subGeoLevel(selectedGeo, selectedTab, catchment)?.charAt(0)?.toUpperCase() + subGeoLevel(selectedGeo, selectedTab, catchment)?.slice(1).replace("_","-")}`}
                                type={TypographyType.h7}
                                colour={TypographyColor.primary}
                                classname='text-start m-0 padding-top-1'
                                style={smallTextStyle}
                            />
                        </div>
                        {isMbpFocused && (
                            <div
                                style={smallTextStyle}
                                onClick={(e) => handleVisitRegion(e,"noncatchment", true)}
                                className="ff-poppins-medium pe-auto padding-right-2 text-decoration-underline cursor-pointer"
                            >
                                Visit <LuArrowUpRight />
                            </div>
                        )}
                    </div>
                    <hr className="m-0 p-0 padding-top-bottom-1" />
                </>
            )}

            {/* Performance and Growth metrics */}
            <div className="d-flex gap-4 p-2 padding-top-0">
                <div className="d-flex gap-1 flex-column">
                    <Heading
                        title={presence.Performance || 'No Data'}
                        type={TypographyType.h7}
                        colour={TypographyColor.secondary}
                        classname='text-start m-0 p-0'
                        style={{ color: presence.performance_color, ...smallTextStyle }}
                    />
                    <Heading
                        title="Performance"
                        type={TypographyType.h7}
                        colour={TypographyColor.muted}
                        classname='text-start m-0 p-0'
                        style={smallTextStyle}
                    />
                </div>
                <div className="d-flex gap-1 flex-column">
                    <Heading
                        title={presence.Growth || 'No Data'}
                        type={TypographyType.h7}
                        colour={TypographyColor.secondary}
                        classname='text-start m-0 p-0'
                        style={{ color: presence.growth_color, ...smallTextStyle }}
                    />
                    <Heading
                        title="Growth"
                        type={TypographyType.h7}
                        colour={TypographyColor.muted}
                        classname='text-start m-0 p-0'
                        style={smallTextStyle}
                    />
                </div>
            </div>

            <hr className="m-0 p-0 margin-top-bottom-1" />

            {/* Catchment message - shown when NOT MBP focused */}
            {hasCatchment && isNotMbpFocused && isNotCatchmentGeoLevel && (
                <div>
                    <Heading
                        title="This presence has a catchment."
                        type={TypographyType.h7}
                        colour={TypographyColor.secondary}
                        classname='text-start m-0 p-2'
                        style={smallTextStyle}
                    />
                    <hr className="m-0 p-0 margin-top-bottom-1" />
                </div>
            )}
            {/* Presence dates and status */}
            <div className="d-flex justify-content-between padding-top-bottom-1 padding-left-2">
                <div>
                    <Heading
                        title={`${presence.presence_start} - ${presence.presence_end}`}
                        type={TypographyType.h7}
                        colour={TypographyColor.primary}
                        classname='text-start m-0 padding-top-1'
                        style={smallTextStyle}
                    />
                    <Heading
                        title={presence.status}
                        type={TypographyType.h7}
                        colour={TypographyColor.primary}
                        classname='text-start m-0 padding-top-1'
                        style={smallTextStyle}
                    />
                </div>
                {isMbpFocused && (
                    <a
                        target="_blank"
                        style={smallTextStyle}
                        href={presence?.google_map_link}
                        className="map-anchor-text ff-poppins-medium pe-auto padding-right-2 text-decoration-underline"
                        onClick={(e) => e.stopPropagation()}
                    >
                        Google Map <LuArrowUpRight />
                    </a>
                )}
            </div>

            {/* Action buttons - shown when MBP is focused */}
            {isMbpFocused  &&  (
                <>
                    <hr className="m-0 p-0 margin-top-1 margin-bottom-3" />
                    <div className={`d-flex align-items-center ${showCatchmentActions ? 'justify-content-between' : 'justify-content-end'} w-100 gap-2 p-2`}>
                        {showCatchmentActions && (
                            <>
                                <div className="pe-auto">
                                    <AddToCompare
                                        isAddedToCompare={isAddedToCompare(presence?.navigation.catchment, compareList, presence?.presence_type)}
                                        geoId={presence?.navigation.catchment}
                                        level={-1}
                                        count={compareList?.[presence?.presence_type]?.length}
                                        tooltipPosition='bottom'
                                        showNodeWithoutHover={true}
                                        parent_id={null}
                                        geoLevel={presence?.presence_type}
                                    />
                                </div>
                                <Button
                                    theme={ButtonTheme.primary}
                                    variant={ButtonVariant.bordered}
                                    size={ButtonSize.xsmall}
                                    type="button"
                                    classname='fs-12 pe-auto'
                                    onClick={(e) => handleVisitRegion(e, 'catchment')}
                                >
                                    View Catchment
                                </Button>
                            </>
                        )}
                        {showVisitButton && (
                            <Button
                                theme={ButtonTheme.primary}
                                variant={ButtonVariant.bordered}
                                size={ButtonSize.xsmall}
                                type="button"
                                classname='fs-12 pe-auto'
                                onClick={(e) => handleVisitRegion(e)}
                            >
                                Visit {subGeoLevel(selectedGeo, selectedTab, catchment)?.charAt(0)?.toUpperCase() + subGeoLevel(selectedGeo, selectedTab, catchment)?.slice(1).replace("_","-")}
                            </Button>
                        )}
                    </div>
                </>
            )}
            {/* Carousel Navigation */}
            {carousel && carousel.markers.length > 1 && isMbpFocused && (
                <div
                    className="d-flex align-items-center justify-content-center gap-1 p-0 m-0"
                    style={carouselStyle}
                >
                    <Button
                        theme={ButtonTheme.primary}
                        variant={ButtonVariant.transparent}
                        type='button'
                        disabled={carousel.currentIndex == 0}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCarouselPrev && onCarouselPrev();
                        }}
                        classname='p-0 m-0 h-auto w-auto pe-auto'
                    >
                        <span className="material-symbols-outlined fs-16 p-0 m-0 padding-top-2">keyboard_arrow_left</span>
                    </Button>
                    <Heading
                        title={`${carousel.currentIndex + 1} of ${carousel.markers.length}`}
                        type={TypographyType.h7}
                        colour={TypographyColor.primary}
                        classname='m-0 p-0'
                    />
                    <Button
                        theme={ButtonTheme.primary}
                        variant={ButtonVariant.transparent}
                        type='button'
                        disabled={carousel.markers.length == carousel.currentIndex + 1}
                        onClick={(e) => {
                            e.stopPropagation();
                            onCarouselNext && onCarouselNext();
                        }}
                        classname='p-0 h-auto w-auto pe-auto'
                    >
                        <span className="material-symbols-outlined fs-16 p-0 m-0 padding-top-2">keyboard_arrow_right</span>
                    </Button>
                </div>
            )}
        </div>
    )
}